import React, { useState, useEffect, useCallback } from 'react';
import { discountsApi, studentsApi, configApi } from '../services/api';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { Textarea } from '../components/ui/textarea';
import { Plus, Check, X, Loader2, Search, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const DiscountsPage = () => {
  const { isAdmin, userBranches } = useAuth();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [searchingStudent, setSearchingStudent] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [statusFilter, setStatusFilter] = useState([]);
  const [branchFilter, setBranchFilter] = useState([]);
  const [classFilter, setClassFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [discountTypes, setDiscountTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [addingType, setAddingType] = useState(false);

  // Multi-discount request state
  const [discountItems, setDiscountItems] = useState([
    { discount_type: '', percentage: '', reason: '', fee_head: 'tuition' }
  ]);
  const [academicYear, setAcademicYear] = useState('2025-2026');

  useEffect(() => {
    configApi.get().then(r => {
      setConfig(r.data);
      setDiscountTypes(r.data.discount_types || []);
    }).catch(() => {});
  }, []);

  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (branchFilter.length) params.branch = branchFilter.join(',');
      if (classFilter.length) params.current_class = classFilter.join(',');
      if (searchQuery) params.search = searchQuery;
      const response = await discountsApi.getAll(params);
      setDiscounts(response.data);
    } catch (err) {
      toast.error('Failed to load discounts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, branchFilter, classFilter, searchQuery]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const handleAddType = async () => {
    if (!newTypeName.trim()) return;
    setAddingType(true);
    try {
      const res = await api.post('/config/discount-types', { name: newTypeName.trim() });
      setDiscountTypes(res.data.discount_types);
      setNewTypeName('');
      toast.success('Discount type added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add type');
    } finally {
      setAddingType(false);
    }
  };

  const handleDeleteType = async (typeName) => {
    try {
      const res = await api.delete(`/config/discount-types/${typeName}`);
      setDiscountTypes(res.data.discount_types);
      toast.success('Discount type removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove type');
    }
  };

  const searchStudents = async (query) => {
    if (!query || query.length < 2) {
      setStudentResults([]);
      return;
    }
    setSearchingStudent(true);
    try {
      const response = await studentsApi.getAll({ search: query, limit: 10 });
      setStudentResults(response.data.students);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchingStudent(false);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setStudentResults([]);
    setStudentSearch('');
  };

  const addDiscountItem = () => {
    setDiscountItems([...discountItems, { discount_type: '', percentage: '', reason: '', fee_head: 'tuition' }]);
  };

  const removeDiscountItem = (index) => {
    if (discountItems.length > 1) {
      setDiscountItems(discountItems.filter((_, i) => i !== index));
    }
  };

  const updateDiscountItem = (index, field, value) => {
    const updated = [...discountItems];
    updated[index][field] = value;
    setDiscountItems(updated);
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!selectedStudent) {
      toast.error('Please select a student');
      return;
    }
    
    // Validate all discount items
    for (let i = 0; i < discountItems.length; i++) {
      if (!discountItems[i].discount_type || !discountItems[i].percentage) {
        toast.error(`Please fill all fields for discount item ${i + 1}`);
        return;
      }
    }
    
    setSubmitting(true);
    try {
      // Submit each discount request
      for (const item of discountItems) {
        await discountsApi.request({
          student_id: selectedStudent.id,
          discount_type: item.discount_type,
          percentage: parseFloat(item.percentage),
          academic_year: academicYear,
          reason: item.reason,
          fee_head: item.fee_head || 'tuition'
        });
      }
      toast.success(`${discountItems.length} discount request(s) submitted for approval`);
      setShowRequestDialog(false);
      setDiscountItems([{ discount_type: '', percentage: '', reason: '', fee_head: 'tuition' }]);
      setSelectedStudent(null);
      fetchDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit discount request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await discountsApi.approve(id);
      toast.success('Discount approved');
      fetchDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve discount');
    }
  };

  const handleReject = async (id) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await discountsApi.reject(id, reason);
      toast.success('Discount rejected');
      fetchDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject discount');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this discount record?')) return;
    try {
      await discountsApi.delete(id);
      toast.success('Discount deleted');
      fetchDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete discount');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-[#5556A8]/10 text-[#5556A8]">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-[#C85A40]/10 text-[#C85A40]">Rejected</Badge>;
      default:
        return <Badge className="bg-[#D99C3B]/10 text-[#D99C3B]">Pending</Badge>;
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = {};
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (branchFilter.length) params.branch = branchFilter.join(',');
      if (classFilter.length) params.current_class = classFilter.join(',');
      if (searchQuery) params.search = searchQuery;
      const response = await api.get('/discounts/export/excel', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `discounts_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch (err) {
      toast.error('Failed to export');
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Discounts</h1>
          <p className="text-[#5C635F] mt-1">Manage student discounts with maker-checker approval</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportExcel} data-testid="export-discounts-excel">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="request-discount-btn">
              <Plus className="w-4 h-4 mr-2" />
              Request Discount
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Discount</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRequest} className="space-y-4">
              {/* Student Search */}
              <div className="space-y-2">
                <Label>Student</Label>
                {selectedStudent ? (
                  <div className="flex items-center justify-between p-3 bg-[#F5F5FA] rounded-lg">
                    <div>
                      <p className="font-medium">{selectedStudent.first_name} {selectedStudent.last_name}</p>
                      <p className="text-sm text-[#5C635F]">{selectedStudent.student_id}</p>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedStudent(null); setNewDiscount({ ...newDiscount, student_id: '' }); }}>
                      Change
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
                    <Input
                      placeholder="Search student..."
                      value={studentSearch}
                      onChange={(e) => { setStudentSearch(e.target.value); searchStudents(e.target.value); }}
                      className="pl-9"
                      data-testid="discount-student-search"
                    />
                    {searchingStudent && <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-[#5C635F]" />}
                    {studentResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-[#D8D9E8] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {studentResults.map((student) => (
                          <button key={student.id} type="button" className="w-full px-3 py-2 text-left hover:bg-[#F5F5FA] flex items-center justify-between" onClick={() => handleStudentSelect(student)}>
                            <span className="font-medium">{student.first_name} {student.last_name}</span>
                            <span className="text-sm text-[#5C635F]">{student.student_id}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Academic Year - shared for all discounts */}
              <div className="space-y-2">
                <Label>Academic Year</Label>
                <Select value={academicYear} onValueChange={setAcademicYear}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(config?.academic_years || ['2024-2025', '2025-2026', '2026-2027']).map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Multiple Discount Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Discount Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addDiscountItem} data-testid="add-discount-item-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Another
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {discountItems.map((item, index) => (
                    <div key={index} className="p-3 bg-[#F5F5FA] rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-[#5C635F]">#{index + 1}</span>
                        {discountItems.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0 text-[#C85A40]" onClick={() => removeDiscountItem(index)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Select value={item.discount_type} onValueChange={(v) => updateDiscountItem(index, 'discount_type', v)}>
                          <SelectTrigger className="h-8 text-sm" data-testid={`discount-type-${index}`}>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {discountTypes.map((type) => (
                              <SelectItem key={type} value={type} className="capitalize">{type.replace(/_/g, ' ')}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={item.fee_head || 'tuition'} onValueChange={(v) => updateDiscountItem(index, 'fee_head', v)}>
                          <SelectTrigger className="h-8 text-sm" data-testid={`discount-feehead-${index}`}>
                            <SelectValue placeholder="Applies to fee" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(config?.fee_head_display || { tuition: 'Tuition' }).map(([k, label]) => (
                              <SelectItem key={k} value={k}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          value={item.percentage}
                          onChange={(e) => updateDiscountItem(index, 'percentage', e.target.value)} 
                          min="0" max="100" step="0.5" 
                          placeholder="Percentage %"
                          className="h-8 text-sm"
                          data-testid={`discount-percentage-${index}`} 
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Single shared reason */}
              <div className="space-y-2">
                <Label>Reason for Discount(s)</Label>
                <Textarea 
                  value={discountItems[0]?.reason || ''} 
                  onChange={(e) => {
                    const updated = discountItems.map(item => ({ ...item, reason: e.target.value }));
                    setDiscountItems(updated);
                  }} 
                  placeholder="Explain the reason for this discount request" 
                  className="min-h-[80px]"
                  data-testid="discount-reason" 
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowRequestDialog(false); setDiscountItems([{ discount_type: '', percentage: '', reason: '', fee_head: 'tuition' }]); setSelectedStudent(null); }}>Cancel</Button>
                <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting} data-testid="submit-discount-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Submit {discountItems.length > 1 ? `${discountItems.length} Requests` : 'Request'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Discount Type Management (Admin) */}
      {isAdmin && (
        <Card className="border-[#D8D9E8]">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-semibold text-[#5C635F]">Manage Discount Types</CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="flex flex-wrap items-center gap-2">
              {discountTypes.map(type => (
                <Badge key={type} variant="secondary" className="bg-[#ECEDF5] text-[#3E3F96] capitalize pr-1 flex items-center gap-1">
                  {type.replace(/_/g, ' ')}
                  <button onClick={() => handleDeleteType(type)} className="ml-1 hover:text-red-500 rounded-full" data-testid={`delete-type-${type}`}>
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              <div className="flex items-center gap-1">
                <Input placeholder="New type..." value={newTypeName} onChange={e => setNewTypeName(e.target.value)}
                  className="h-7 w-36 text-sm" data-testid="new-discount-type-input"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddType(); } }} />
                <Button variant="outline" size="sm" onClick={handleAddType} disabled={addingType || !newTypeName.trim()} className="h-7" data-testid="add-discount-type-btn">
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#5C635F]" />
              <Input
                placeholder="Search by student name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="discount-search"
              />
            </div>
            <MultiSelect widthClass="w-full sm:w-36" placeholder="All Branches" testId="discount-branch-filter"
              options={(isAdmin ? config?.branches : userBranches) || []}
              selected={branchFilter} onChange={setBranchFilter} />
            <MultiSelect widthClass="w-full sm:w-36" placeholder="All Classes" testId="discount-class-filter"
              options={config?.classes || []}
              selected={classFilter} onChange={setClassFilter} />
            <MultiSelect widthClass="w-full sm:w-40" placeholder="All Status" testId="discount-status-filter"
              options={[{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }]}
              selected={statusFilter} onChange={setStatusFilter} />
            {(searchQuery || branchFilter.length || classFilter.length || statusFilter.length) && (
              <Button variant="ghost" size="sm" className="text-[#5C635F]" onClick={() => { 
                setSearchQuery(''); setBranchFilter([]); setClassFilter([]); setStatusFilter([]);
              }}>Clear filters</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Discounts table */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
            </div>
          ) : discounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#5C635F]">
              <p>No discount requests found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5FA]">
                    <TableHead className="text-xs uppercase font-semibold">Student</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Branch</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Type</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Applies To</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Percentage</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Reason</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Year</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Requested</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                    {isAdmin && <TableHead className="text-xs uppercase font-semibold">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {discounts.map((discount) => (
                    <TableRow key={discount.id}>
                      <TableCell className="font-medium">{discount.student_name}</TableCell>
                      <TableCell className="text-[#5C635F]">{discount.branch || '-'}</TableCell>
                      <TableCell className="capitalize">{discount.discount_type?.replace(/_/g, ' ')}</TableCell>
                      <TableCell className="text-[#5C635F]">{config?.fee_head_display?.[discount.fee_head || 'tuition'] || (discount.fee_head || 'Tuition')}</TableCell>
                      <TableCell className="tabular-nums font-medium text-[#3E3F96]">{discount.percentage}%</TableCell>
                      <TableCell className="text-[#5C635F] text-sm max-w-[200px] truncate" title={discount.reason}>{discount.reason || '-'}</TableCell>
                      <TableCell>{discount.academic_year}</TableCell>
                      <TableCell className="text-[#5C635F]">{formatDate(discount.requested_at)}</TableCell>
                      <TableCell>{getStatusBadge(discount.status)}</TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div className="flex gap-1">
                            {discount.status === 'pending' && (
                              <>
                                <Button variant="ghost" size="sm" className="text-[#5556A8]" onClick={() => handleApprove(discount.id)} data-testid={`approve-${discount.id}`}>
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-[#C85A40]" onClick={() => handleReject(discount.id)} data-testid={`reject-${discount.id}`}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button variant="ghost" size="sm" className="text-[#C85A40]" onClick={() => handleDelete(discount.id)} data-testid={`delete-discount-${discount.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DiscountsPage;
